import { Sparkles, Workflow, type LucideIcon } from "lucide-react"
import { SidebarTooltip } from "@/components/ui/sidebar-tooltip"
import { UI_ACCENT_FILLED } from "@/lib/ui-selection"
import { cn } from "@/lib/utils"
import type { ShotViewMode } from "@/stores/shot-panel-store"

type ShotViewModeToggleProps = {
  value: ShotViewMode
  onChange: (mode: ShotViewMode) => void
  classicLabel: string
  flowLabel: string
}

const SEGMENT_BASE_CLASS =
  "relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg transition-colors"
const SEGMENT_SELECTED_CLASS = cn(UI_ACCENT_FILLED, "dark:shadow-lg dark:shadow-violet-600/30 light:shadow-none")
const SEGMENT_IDLE_CLASS =
  "text-ui-text-secondary hover:text-ui-text-primary dark:bg-zinc-800 hover:dark:bg-zinc-700 light:bg-clay-2/90 light:hover:bg-clay-3/90"

export function ShotViewModeToggle({ value, onChange, classicLabel, flowLabel }: ShotViewModeToggleProps) {
  const segments: { mode: ShotViewMode; label: string; icon: LucideIcon }[] = [
    { mode: "classic", label: classicLabel, icon: Sparkles },
    { mode: "flow", label: flowLabel, icon: Workflow },
  ]

  return (
    <div
      role="radiogroup"
      aria-label={`${classicLabel}, ${flowLabel}`}
      className="flex flex-row items-center gap-1.5 md:flex-col md:rounded-lg md:border md:border-ui-border md:bg-ui-bg-elevated md:p-2"
    >
      {segments.map(({ mode, label, icon: Icon }) => {
        const selected = value === mode
        return (
          <SidebarTooltip key={mode} label={label} elevated>
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={label}
              onClick={() => onChange(mode)}
              className={cn(SEGMENT_BASE_CLASS, selected ? SEGMENT_SELECTED_CLASS : SEGMENT_IDLE_CLASS)}
            >
              <Icon className="h-4 w-4" strokeWidth={2} />
            </button>
          </SidebarTooltip>
        )
      })}
    </div>
  )
}
